import { 
  Folder, 
  File, 
  Image, 
  Video, 
  Music, 
  FileText, 
  Archive, 
  Download,
  ExternalLink,
  MoreVertical,
  Trash2,
  Edit3,
  Eye,
  Edit,
  Users,
  Lock,
  Copy,
  Star,
  StarOff,
  Settings,
  FileArchive  // ✅ Added for Extract ZIP icon
} from 'lucide-react'
import { useState, useEffect } from 'react'
import type { FileSystemItem } from '@shared'
import { formatFileSize, formatDate, getFileType, api } from '../utils/api'
import { useFileStore } from '../stores/fileStore'
import { useAuthStore } from '../stores/authStore'
import { useToastStore } from '../stores/toastStore'

interface FileItemProps {
  item: FileSystemItem
  viewMode: 'grid' | 'list'
  isSelected: boolean
  onSelect: (id: string) => void
  onNavigate?: (path: string) => void
  onRename?: (item: FileSystemItem) => void
  onDelete?: (item: FileSystemItem) => void
  onView?: (item: FileSystemItem) => void
  onEdit?: (item: FileSystemItem) => void
  onUnzip?: (item: FileSystemItem) => void  // ✅ Added unzip callback
}

import { publicFileUrl } from '../config'
import { toggleFavorite, isFavorite } from './RecentAndFavoritesSidebar'

// Thumbnail component for images
const ImageThumbnail: React.FC<{ 
  src: string; 
  alt: string; 
  className?: string;
  fallback?: React.ReactNode;
}> = ({ src, alt, className, fallback }) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  const handleLoad = () => {
    setLoaded(true);
    setError(false);
  };

  const handleError = () => {
    setError(true);
    setLoaded(false);
  };

  if (error && fallback) {
    return <>{fallback}</>;
  }

  return (
    <div className={`relative overflow-hidden ${className}`}>
      <img
        src={src}
        alt={alt}
        className={`w-full h-full object-cover transition-opacity duration-300 ${
          loaded ? 'opacity-100' : 'opacity-0'
        }`}
        onLoad={handleLoad}
        onError={handleError}
        loading="lazy"
      />
      {!loaded && !error && (
        <div className="absolute inset-0 flex items-center justify-center bg-dark-700 animate-pulse">
          <Image className="w-4 h-4 text-dark-500" />
        </div>
      )}
    </div>
  );
};

export function FileItem({ 
  item, 
  viewMode, 
  isSelected, 
  onSelect, 
  onNavigate, 
  onRename,
  onDelete,
  onView,
  onEdit,
  onUnzip  // ✅ Added to destructuring
}: FileItemProps) {
  const [showContextMenu, setShowContextMenu] = useState(false)
  const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 })
  const [isSharedFolder, setIsSharedFolder] = useState(false)
  const { deleteItem } = useFileStore()
  const { token, csrfToken } = useAuthStore()
  const { addToast } = useToastStore()

  const isFolder = item.type === 'folder'
  const data = item.item as any

  // Check if file is editable
  const isEditable = !isFolder && data.isEditable && data.canEdit

  // Check if file is an image
  const isImage = !isFolder && data.mimeType && data.mimeType.startsWith('image/')

  // ✅ Check if file is a ZIP archive
  const isZipFile = !isFolder && data.mimeType && 
    (data.mimeType === 'application/zip' || 
     data.mimeType === 'application/x-zip-compressed' ||
     data.name.endsWith('.zip'))

  // Check if this folder is a shared folder
  useEffect(() => {
    if (isFolder) {
      checkIfSharedFolder()
    }
  }, [isFolder, data.name])

  const checkIfSharedFolder = async () => {
    try {
      const response = await fetch(`/api/shared-folders/check/${data.name}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      
      if (response.ok) {
        const result = await response.json()
        if (result.success && result.data.isShared) {
          setIsSharedFolder(true)
        }
      }
    } catch (error) {
      // Not a shared folder or error - ignore
    }
  }

  const copySharedURL = async () => {
    const url = `${window.location.origin}/${data.name}`
    try {
      await navigator.clipboard.writeText(url)
      addToast({
        type: 'success',
        message: `Shared URL copied: ${url}`
      })
    } catch (error) {
      addToast({
        type: 'error',
        message: 'Failed to copy URL'
      })
    }
    setShowContextMenu(false)
  }

  // Copy the direct public URL for this file (reroutes amp/* to active AMP host).
  const copyFileURL = async () => {
    const url = publicFileUrl(data.path)
    try {
      await navigator.clipboard.writeText(url)
      addToast({ type: 'success', message: 'URL copied to clipboard' })
    } catch {
      addToast({ type: 'error', message: 'Failed to copy URL' })
    }
    setShowContextMenu(false)
  }

  const [pinned, setPinned] = useState(() => isFavorite({ path: data.path, type: isFolder ? 'folder' : 'file' }))

  const toggleFavoritePin = () => {
    const nowPinned = toggleFavorite({
      path: data.path,
      name: data.name,
      type: isFolder ? 'folder' : 'file',
    })
    setPinned(nowPinned)
    addToast({
      type: 'success',
      message: nowPinned ? `Pinned ${data.name}` : `Unpinned ${data.name}`,
    })
    setShowContextMenu(false)
  }

  const manageSharedFolder = () => {
    // Open shared folder management modal
    addToast({
      type: 'info',
      message: 'Shared folder management coming soon!'
    })
    setShowContextMenu(false)
  }

  const removeSharing = async () => {
    if (confirm('Remove sharing from this folder? Users will lose access.')) {
      try {
        const response = await fetch(`/api/shared-folders/remove`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'X-CSRF-Token': csrfToken || '',
          },
          body: JSON.stringify({ folderName: data.name })
        })

        if (response.ok) {
          setIsSharedFolder(false)
          addToast({
            type: 'success',
            message: 'Folder sharing removed'
          })
        }
      } catch (error) {
        addToast({
          type: 'error',
          message: 'Failed to remove sharing'
        })
      }
    }
    setShowContextMenu(false)
  }

  // Get thumbnail URL for images — reroutes amp/* to active AMP subdomain.
  const getThumbnailUrl = () => {
    if (isImage && data.path) {
      return publicFileUrl(data.path);
    }
    return null;
  };

  // Close context menu when clicking outside
  useEffect(() => {
    if (showContextMenu) {
      const handleClickOutside = (e: MouseEvent) => {
        const target = e.target as Element
        if (!target.closest('.context-menu-container')) {
          setShowContextMenu(false)
        }
      }
      
      document.addEventListener('click', handleClickOutside)
      return () => document.removeEventListener('click', handleClickOutside)
    }
  }, [showContextMenu])

  const handleClick = () => {
    if (isFolder && onNavigate) {
      onNavigate(data.path)
    } else {
      onSelect(data.id)
    }
  }

  const handleDoubleClick = () => {
    if (!isFolder) {
      if (onView) {
        onView(item)
      } else {
        const fileUrl = api.getPublicFileUrl(data.path)
        window.open(fileUrl, '_blank')
      }
    }
  }

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    
    const x = Math.min(e.clientX, window.innerWidth - 200)
    const y = Math.min(e.clientY, window.innerHeight - 300)
    
    setContextMenuPosition({ x, y })
    setShowContextMenu(true)
  }

  const handleMenuButtonClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    
    const rect = e.currentTarget.getBoundingClientRect()
    const x = Math.min(rect.right - 160, window.innerWidth - 200)
    const y = Math.min(rect.bottom + 8, window.innerHeight - 300)
    
    setContextMenuPosition({ x, y })
    setShowContextMenu(!showContextMenu)
  }

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation()
    setShowContextMenu(false)
    if (onDelete) {
      onDelete(item)
    } else {
      if (confirm(`Are you sure you want to delete this ${isFolder ? 'folder' : 'file'}?`)) {
        await deleteItem(data.id, item.type)
      }
    }
  }

  const handleView = (e: React.MouseEvent) => {
    e.stopPropagation()
    setShowContextMenu(false)
    if (onView) {
      onView(item)
    } else {
      if (!isFolder) {
        const fileUrl = api.getPublicFileUrl(data.path)
        window.open(fileUrl, '_blank')
      }
    }
  }

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation()
    setShowContextMenu(false)
    if (onEdit && isEditable) {
      onEdit(item)
    }
  }

  // ✅ NEW: Handle unzip action
  const handleUnzip = (e: React.MouseEvent) => {
    e.stopPropagation()
    setShowContextMenu(false)
    if (onUnzip && isZipFile) {
      onUnzip(item)
    }
  }

  const handleDownload = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!isFolder) {
      const downloadUrl = api.getDownloadUrl(data.path)
      window.open(downloadUrl, '_blank')
    }
    setShowContextMenu(false)
  }

  const handleRename = (e: React.MouseEvent) => {
    e.stopPropagation()
    setShowContextMenu(false)
    if (onRename) {
      onRename(item)
    }
  }

  const handleOpen = (e: React.MouseEvent) => {
    e.stopPropagation()
    setShowContextMenu(false)
    if (!isFolder) {
      const url = api.getPublicFileUrl(data.path)
      window.open(url, '_blank')
    }
  }

  const getFileIcon = () => {
    if (isFolder) {
      return <Folder className={`h-6 w-6 ${isSharedFolder ? 'text-green-400' : 'text-blue-400'}`} />
    }

    const fileType = getFileType(data.mimeType)
    switch (fileType) {
      case 'image':
        return <Image className="h-6 w-6 text-green-400" />
      case 'video':
        return <Video className="h-6 w-6 text-purple-400" />
      case 'audio':
        return <Music className="h-6 w-6 text-pink-400" />
      case 'text':
        return <FileText className="h-6 w-6 text-gray-400" />
      case 'archive':
        return <Archive className="h-6 w-6 text-yellow-400" />
      case 'document':
        return <FileText className="h-6 w-6 text-blue-400" />
      default:
        return <File className="h-6 w-6 text-gray-400" />
    }
  }

  const getDisplayName = () => {
    return data.name
  }

  const getSubtitle = () => {
    if (isFolder) {
      const count = data.childCount || 0
      return `${count} item${count !== 1 ? 's' : ''}`
    }
    const subtitle = `${formatFileSize(data.size)} • ${formatDate(data.uploadDate)}`
    if (isEditable) {
      return `${subtitle} • Editable`
    }
    return subtitle
  }

  const getVisibilityBadge = () => {
    if (isSharedFolder) {
      return (
        <span className="inline-flex items-center rounded-full px-2 py-1 text-xs font-medium bg-green-500/20 text-green-400">
          <Users className="w-3 h-3 mr-1" />
          Shared
        </span>
      )
    }
    
    return (
      <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
        data.isPublic
          ? 'bg-green-500/20 text-green-400'
          : 'bg-red-500/20 text-red-400'
      }`}>
        {data.isPublic ? 'Public' : 'Private'}
      </span>
    )
  }

  // Shared context menu — identical in list and grid views. Closures capture
  // all handlers/flags, so no prop-drilling. (Previously duplicated per view;
  // grid was missing the Pin favorite entry — unifying fixes that.)
  const contextMenu = showContextMenu ? (
    <div className="context-menu-container">
      <div
        className="fixed inset-0 z-40"
        onClick={() => setShowContextMenu(false)}
      />
      <div
        className="fixed z-50 min-w-48 rounded-lg border border-dark-600 bg-dark-800 py-2 shadow-xl"
        style={{ left: contextMenuPosition.x, top: contextMenuPosition.y }}
      >
        {/* Shared folder actions */}
        {isSharedFolder && (
          <>
            <button
              onClick={copySharedURL}
              className="w-full px-4 py-2 text-left text-sm text-green-300 hover:bg-dark-700 hover:text-green-200 flex items-center"
            >
              <Copy className="mr-2 h-4 w-4" />
              Copy Share URL
            </button>
            <button
              onClick={manageSharedFolder}
              className="w-full px-4 py-2 text-left text-sm text-green-300 hover:bg-dark-700 hover:text-green-200 flex items-center"
            >
              <Settings className="mr-2 h-4 w-4" />
              Manage Sharing
            </button>
            <button
              onClick={removeSharing}
              className="w-full px-4 py-2 text-left text-sm text-orange-400 hover:bg-orange-500/10 hover:text-orange-300 flex items-center"
            >
              <Lock className="mr-2 h-4 w-4" />
              Remove Sharing
            </button>
            <div className="my-1 h-px bg-dark-600" />
          </>
        )}

        {/* File actions */}
        {!isFolder && (
          <>
            <button
              onClick={handleView}
              className="w-full px-4 py-2 text-left text-sm text-dark-300 hover:bg-dark-700 hover:text-white flex items-center"
            >
              <Eye className="mr-2 h-4 w-4" />
              View
            </button>
            <button
              onClick={copyFileURL}
              className="w-full px-4 py-2 text-left text-sm text-dark-300 hover:bg-dark-700 hover:text-white flex items-center"
            >
              <Copy className="mr-2 h-4 w-4" />
              Copy URL
            </button>
          </>
        )}

        {/* Pin/Unpin favorite — works for BOTH files and folders */}
        <button
          onClick={toggleFavoritePin}
          className="w-full px-4 py-2 text-left text-sm text-dark-300 hover:bg-dark-700 hover:text-white flex items-center"
        >
          {pinned
            ? <><StarOff className="mr-2 h-4 w-4 text-yellow-400" /> Unpin favorite</>
            : <><Star className="mr-2 h-4 w-4" /> Pin favorite</>}
        </button>

        {/* Edit option for editable files */}
        {isEditable && (
          <button
            onClick={handleEdit}
            className="w-full px-4 py-2 text-left text-sm text-dark-300 hover:bg-dark-700 hover:text-white flex items-center"
          >
            <Edit className="mr-2 h-4 w-4" />
            Edit
          </button>
        )}

        {/* Extract ZIP option */}
        {isZipFile && onUnzip && (
          <button
            onClick={handleUnzip}
            className="w-full px-4 py-2 text-left text-sm text-dark-300 hover:bg-dark-700 hover:text-white flex items-center"
          >
            <FileArchive className="mr-2 h-4 w-4" />
            Extract ZIP
          </button>
        )}

        <button
          onClick={handleRename}
          className="w-full px-4 py-2 text-left text-sm text-dark-300 hover:bg-dark-700 hover:text-white flex items-center"
        >
          <Edit3 className="mr-2 h-4 w-4" />
          Rename
        </button>

        {!isFolder && (
          <>
            <button
              onClick={handleDownload}
              className="w-full px-4 py-2 text-left text-sm text-dark-300 hover:bg-dark-700 hover:text-white flex items-center"
            >
              <Download className="mr-2 h-4 w-4" />
              Download
            </button>
            <button
              onClick={handleOpen}
              className="w-full px-4 py-2 text-left text-sm text-dark-300 hover:bg-dark-700 hover:text-white flex items-center"
            >
              <ExternalLink className="mr-2 h-4 w-4" />
              Open
            </button>
          </>
        )}

        <div className="my-1 h-px bg-dark-600" />
        <button
          onClick={handleDelete}
          className="w-full px-4 py-2 text-left text-sm text-red-400 hover:bg-red-500/10 hover:text-red-300 flex items-center"
        >
          <Trash2 className="mr-2 h-4 w-4" />
          Delete{isSharedFolder ? ' & Remove Sharing' : ''}
        </button>
      </div>
    </div>
  ) : null

  if (viewMode === 'list') {
    return (
      <div className="relative">
        <div 
          className={`group flex items-center justify-between rounded-lg border p-3 transition-all hover:bg-dark-700/50 ${
            isSelected ? 'border-primary-500 bg-primary-500/10' : 'border-dark-600'
          } ${isSharedFolder ? 'ring-1 ring-green-500/30' : ''}`}
          onClick={handleClick}
          onDoubleClick={handleDoubleClick}
          onContextMenu={handleContextMenu}
        >
          <div className="flex items-center space-x-3">
            <button
              onClick={(e) => {
                e.stopPropagation()
                onSelect(data.id)
              }}
              className="flex h-4 w-4 items-center justify-center rounded border border-dark-500"
            >
              {isSelected && <div className="h-2 w-2 rounded bg-primary-500" />}
            </button>
            
            {/* Icon or Thumbnail for list view */}
            <div className="flex-shrink-0 relative">
              {isImage ? (
                <div className="w-8 h-8 rounded border border-dark-600 overflow-hidden">
                  <ImageThumbnail
                    src={getThumbnailUrl()!}
                    alt={data.name}
                    className="w-full h-full"
                    fallback={getFileIcon()}
                  />
                </div>
              ) : (
                getFileIcon()
              )}
              {isSharedFolder && (
                <Users className="absolute -top-1 -right-1 h-3 w-3 text-green-400 bg-dark-800 rounded-full" />
              )}
            </div>
            
            <div>
              <div className="flex items-center gap-2">
                <p className="font-medium text-white">{getDisplayName()}</p>
                {isEditable && (
                  <Edit className="h-3 w-3 text-green-400" />
                )}
                {isSharedFolder && (
                  <Lock className="h-3 w-3 text-green-400" />
                )}
              </div>
              <p className="text-sm text-dark-400">{getSubtitle()}</p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            {getVisibilityBadge()}
            
            <button
              onClick={handleMenuButtonClick}
              className="rounded-md p-1 text-dark-400 opacity-0 hover:bg-dark-600 hover:text-white group-hover:opacity-100"
            >
              <MoreVertical className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Enhanced Context Menu */}
        {contextMenu}
      </div>
    )
  }

  // Grid view
  return (
    <div className="relative">
      <div 
        className={`group relative rounded-lg border p-4 transition-all hover:bg-dark-700/50 ${
          isSelected ? 'border-primary-500 bg-primary-500/10' : 'border-dark-600'
        } ${isSharedFolder ? 'ring-1 ring-green-500/30' : ''}`}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        onContextMenu={handleContextMenu}
      >
        <div className="absolute right-2 top-2">
          <button
            onClick={(e) => {
              e.stopPropagation()
              onSelect(data.id)
            }}
            className="flex h-4 w-4 items-center justify-center rounded border border-dark-500 opacity-0 group-hover:opacity-100"
          >
            {isSelected && <div className="h-2 w-2 rounded bg-primary-500" />}
          </button>
        </div>

        {/* Main icon/thumbnail area */}
        <div className="mb-3 flex justify-center">
          <div className="relative">
            {isImage ? (
              <div className="w-16 h-16 rounded-lg border border-dark-600 overflow-hidden bg-dark-700">
                <ImageThumbnail
                  src={getThumbnailUrl()!}
                  alt={data.name}
                  className="w-full h-full"
                  fallback={
                    <div className="w-full h-full flex items-center justify-center">
                      {getFileIcon()}
                    </div>
                  }
                />
              </div>
            ) : isFolder ? (
              <div className={`w-16 h-16 flex items-center justify-center rounded-lg ${isSharedFolder ? 'bg-green-500/20' : 'bg-dark-700'}`}>
                <Folder className={`w-8 h-8 ${isSharedFolder ? 'text-green-400' : 'text-blue-400'}`} />
              </div>
            ) : (
              <div className="w-16 h-16 flex items-center justify-center rounded-lg bg-dark-700">
                {getFileIcon()}
              </div>
            )}
            {isSharedFolder && (
              <Users className="absolute -top-1 -right-1 h-4 w-4 text-green-400 bg-dark-800 rounded-full p-0.5" />
            )}
          </div>
        </div>

        <div className="mb-2">
          <div className="flex items-center justify-center gap-1 mb-1">
            <h3 className="truncate font-medium text-white text-center text-sm" title={getDisplayName()}>
              {getDisplayName()}
            </h3>
            {isEditable && (
              <Edit className="h-3 w-3 text-green-400 flex-shrink-0" />
            )}
            {isSharedFolder && (
              <Lock className="h-3 w-3 text-green-400 flex-shrink-0" />
            )}
          </div>
          <p className="text-xs text-dark-400 text-center">{getSubtitle()}</p>
        </div>

        <div className="flex items-center justify-between">
          {getVisibilityBadge()}
          
          <button
            onClick={handleMenuButtonClick}
            className="rounded-md p-1 text-dark-400 opacity-0 hover:bg-dark-600 hover:text-white group-hover:opacity-100"
          >
            <MoreVertical className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Context menu (same as list view) */}
      {contextMenu}
    </div>
  )
}